import React, {useEffect, useMemo, useState} from 'react';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {UnmountClosed} from 'react-collapse';
import {Select, Space, Tabs} from 'antd';
import {AnimatePresence} from 'framer-motion';
import dayjs from 'dayjs';
import BalancePageHeader from '../../components/pages/TransactionsPage/BalancePageHeader';
import DashboardPageTransition from '../../components/animations/DashboardPageTransition';
import PageDocumentDetails from '../../components/PageDocumentDetails';
import CompleteKycCard from '../../components/pages/OverviewPage/CompleteKycCard';
import {
  StyledOverviewPageContainer,
  StyledOverviewPageContent,
  StyledOverviewPageSpin,
  StyledOverviewPageCard,
} from './StyledOverviewPage';
import {
  useIsFeaturesLoaded,
  useIsEmployeeChanged,
  useIsRequiredCardVerification,
  useIsTreezorEnabled,
  useFeatureAvailable
} from '../../hooks';
import {overviewActions} from '../../state/actions/overview.actions';
import {transactionActions} from '../../state/actions/transaction.actions';
import RightSidebar from '../../components/animations/RightSidebar';
import Budgets from '../../components/pages/OverviewPage/Budgets';
import Expenses from '../../components/pages/OverviewPage/Expenses';
import Invoices from '../../components/pages/OverviewPage/Invoices';
import Spending from '../../components/pages/OverviewPage/Spending';
import OverviewDropdown from '../../components/pages/OverviewPage/OverviewDropdown';
import AddExpenseButton from '../../components/pages/ExpensesPage/AddExpenseButton';
// import OverviewRightPanel from '../../components/pages/OverviewPage/OverviewRightPanel';
import {firebaseEvents} from '../../snippets/firebase';
import {
  dateHelpers,
  formHelpers,
  kycHelpers,
  subscriptionsHelpers,
  systemHelpers
} from '../../utils/helpers';
import {dateFilterRanges, expendituresTypes, overviewPageUtils} from './utils';
import {companyFeaturesConstants} from '../../constants';

const counterDefaultProps = {value: null, isLoaded: false};

const {getCurrentDate} = dateHelpers;

const {month, year} = getCurrentDate();

const {CURRENT_MONTH, PREVIOUS_MONTH, YEARLY} = dateFilterRanges;

const getRangeDate = (value) => {
  const methods = {
    [CURRENT_MONTH]: () => ({month, year}),
    [PREVIOUS_MONTH]: () => {
      const previousMonthDate = dayjs().subtract(1, 'month');
      return {month: previousMonthDate.month() + 1, year: previousMonthDate.year()}
    },
  }
  return methods.hasOwnProperty(value) ? methods[value]() : undefined;
}

const getMonthRangeLabel = (startDateObj, endDateObj) => {
  const month = startDateObj.format('MMMM');
  const endDayNum = parseInt(endDateObj.format('D'));
  let label = `${month} 1`;
  if (endDayNum !== 1) label = `${label}-${endDayNum}`;
  return label;
}

const defaultSpaceProps = {
  size: 'large', style: {width: '100%'}
}

const dateFormat = 'YYYY-MM';

const defaultSpendingData = {
  loaded: false,
  query: { month, year },
  chartData: [],
  chartDataServices: [],
  spending: [],
  yearly: [],
  yearlyByServices: []
}

const OverviewPage = () => {
  const [t] = useTranslation(['main', 'subscriptions']);
  const [showLoader, setShowLoader] = useState(true);
  // const [otherSpendingData, setOtherSpendingData] = useState({
  //   categories: [],
  //   costCenter: [],
  //   variations: [],
  // });
  const [actualSpendingData, setActualSpendingData] = useState(defaultSpendingData);
  const [expectedSpendingData, setExpectedSpendingData] = useState(defaultSpendingData);
  // const [upcomingSpending, setUpcomingSpending] = useState([]);
  const [totals, setTotals] = useState({
    actual: counterDefaultProps,
    expected: counterDefaultProps,
  });
  const [requiredCompleteKyc, setRequiredCompleteKyc] = useState(false);
  const [filterProps, setFilterProps] = useState({
    range: CURRENT_MONTH,
    tags: [],
    type: expendituresTypes.ALL
  });
  const chartView = 'all';
  const [isOpenDetails, setIsOpenDetails] = useState(false);

  const { bankingCompanyState, companyCountry, costCenterTags, isKnownEmployee } = useSelector(({ user, banking, company }) => ({
    isKnownEmployee: user.isKnownEmployee,
    bankingCompanyState: banking.companyState,
    companyCountry: company.country,
    costCenterTags: company.costCenterTags,
  }));

  const dispatch = useDispatch();

  const isEmployeeChanged = useIsEmployeeChanged();

  const {required: isRequiredCardVerification} = useIsRequiredCardVerification();

  const isTreezorEnabled = useIsTreezorEnabled();
  const isFeaturesLoaded = useIsFeaturesLoaded();

  const isBudgetsAvailable = useFeatureAvailable(companyFeaturesConstants.BUDGETS_PO_AVAILABLE);
  const isBankingDisabled = useFeatureAvailable(companyFeaturesConstants.TREEZOR_DISABLED);

  const isTabsAvailable = isBudgetsAvailable && isBankingDisabled;

  const isEmptySpending = useMemo(() => {
    return isRequiredCardVerification || requiredCompleteKyc;
  }, [isRequiredCardVerification, requiredCompleteKyc]);

  const actualTimeRange = useMemo(() => {
    const range = filterProps.range;
    const timeRangeActions = {
      [CURRENT_MONTH]: () => {
        const date = moment();
        return getMonthRangeLabel(date, date);
      },
      [PREVIOUS_MONTH]: () => {
        const startDate = moment().subtract(1, 'month');
        const endDate = startDate.endOf('month');
        return getMonthRangeLabel(startDate, endDate);
      },
      [YEARLY]: () => {
        const monthYearFormat = 'MMM YY';
        const currentMonth = moment().format(monthYearFormat);
        const startMonth = moment().subtract(11, 'months').format(monthYearFormat);
        return `${startMonth}-${currentMonth}`;
      }
    }
    return timeRangeActions.hasOwnProperty(range) ? timeRangeActions[range]() : '';
  }, [filterProps]);

  const finishLoading = () => setShowLoader(false);

  const startLoadingTotals = () => {
    setTotals({
      actual: counterDefaultProps,
      expected: counterDefaultProps
    });
  }

  const clearTotals = () => {
    setTotals({
      actual: {value: null, isLoaded: true},
      expected: {value: null, isLoaded: true}
    });
  }

  const calculateTotals = ({ actualSpendingData, expectedSpendingData, variant } = {}) => {
    if (!actualSpendingData || !expectedSpendingData) return;

    const isYearlySpending = variant === YEARLY;
    const getSpending = (data) => (isYearlySpending ? data.yearly : data.spending) || [];

    const calculateTotal = (spending) =>
      spending.reduce((sum, item) =>
        sum + subscriptionsHelpers.getSpendingServicesAmount(
          isYearlySpending ? { services: [item] } : item, 'amount'), 0
      );

    const actual = calculateTotal(getSpending(actualSpendingData));
    const expected = calculateTotal(getSpending(expectedSpendingData));

    setTotals({
      actual: { isLoaded: actualSpendingData.loaded, value: actual },
      expected: { isLoaded: expectedSpendingData.loaded, value: expected }
    });
  };

  const mainChartData = useMemo(() => {
    const expenditure = filterProps?.type;
    const range = filterProps?.range;
    const actualSpending = actualSpendingData.spending;
    const expectedSpending = expectedSpendingData.spending;

    return {
      chartData: overviewPageUtils.calculateSubscriptionsChart({
        actualSpending,
        expectedSpending,
        expenditure,
        range,
      }),
      chartDataServices: overviewPageUtils.formatChartDataServices({
        actualSpending,
        expectedSpending,
      }),
      yearly: overviewPageUtils.formatYearlySpending(actualSpendingData.yearly, expectedSpendingData.yearly),
      yearlyByServices: overviewPageUtils.formatYearlyByServicesSpending(actualSpendingData.yearlyByServices)
    }
  }, [filterProps?.range, filterProps.type, actualSpendingData, expectedSpendingData]);

  const chartData = useMemo(() => {
    let data = mainChartData.chartData || [];

    if (filterProps.range !== YEARLY) {
      const actualTotal = totals?.actual?.value || 0;
      const expectedTotal = totals?.expected?.value || 0;

      // Determine the active day, defaulting to the last item if none is
      const lastDay = data[data.length - 1];
      const activeDay = data.find(item => item.isActive) || lastDay;

      if (activeDay) {
        const currentNormalizationRatio = actualTotal / (activeDay.current || 1);
        const expectedNormalizationRatio = expectedTotal / (lastDay.expected || 1);

        // Update the data items
        data = data.map((item) => {
          const updatedItem = { ...item };
          const { current, expected } = item;

          // Update `current` values if defined
          if (current !== undefined) {
            updatedItem.current = current === 0
              ? currentNormalizationRatio
              : current * currentNormalizationRatio;
          }

          // Update `expected` values if defined
          if (expected !== undefined) {
            updatedItem.expected = expectedTotal > 0 ? (expected || 1) * expectedNormalizationRatio : expected;
          }

          return updatedItem;
        });
      }
    }

    return data;
  }, [totals, mainChartData.chartData, filterProps]);

  const counterData = useMemo(() => {
    if (filterProps.range !== CURRENT_MONTH) return totals;

    const todayDate = dayjs().format('YYYY-MM-DD');
    const expectedSpending = chartData.find(i => i.date === todayDate)?.expected || 0;

    return {
      ...totals,
      expected: {...totals.expected, value: expectedSpending}
    }
  }, [filterProps, chartData, totals])

  useEffect(() => {
    const isDefaultRequestType = checkIsDefaultRequestType(filterProps);
    const range = filterProps?.range;
    const isYearlyRange = range === YEARLY;
    if (isYearlyRange || !isDefaultRequestType) {
      startLoadingTotals();
      calculateTotals({
        actualSpendingData,
        expectedSpendingData,
        variant: isYearlyRange ? 'yearly' : 'monthly'
      });
    }
  }, [actualSpendingData, expectedSpendingData]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkIsDefaultRequestType = (query) => (query?.type || expendituresTypes.ALL) === expendituresTypes.ALL;

  const getOverviewData = ({
    range = CURRENT_MONTH,
    query = undefined,
    variant = 'monthly'
  } = {}) => {
    let requestQuery = query || actualSpendingData.query;
    const endpointFunc = variant === 'monthly' ? overviewActions.getOverview : overviewActions.getYearlyOverview;

    // mark that data is not loaded yet
    setShowLoader(true);
    setActualSpendingData({...actualSpendingData, loaded: false});
    setExpectedSpendingData({...expectedSpendingData, loaded: false});

    getPreviousOverviewData({range, query, variant});

    if (range === YEARLY) startLoadingTotals();

    dispatch(endpointFunc(
      requestQuery,
      (data) => {
        // const gDataP = (name) => data?.[name] || [];
        // const categories = gDataP('categories').filter(c => c.category);
        // const costCenter = gDataP('cost_center');
        // const upcoming = gDataP('upcoming');
        // const variations = gDataP('variations');
        // const isCurrentMonthRange = range === CURRENT_MONTH;

        // const latestUpcomingSpending = isCurrentMonthRange ? overviewPageUtils.formatUpcoming(upcoming) : upcomingSpending;

        setActualSpendingData({
          ...actualSpendingData,
          ...overviewPageUtils.getSpendingData(data),
          loaded: true,
        });

        // setOtherSpendingData({
        //   categories: overviewPageUtils.formatCategories(categories, t),
        //   costCenter: overviewPageUtils.formatCostCenter(costCenter),
        //   variations: overviewPageUtils.formatVariations(variations),
        // });

        // setUpcomingSpending(latestUpcomingSpending);
        finishLoading();

        // load upcoming spending if only 10 days until end of month
        // if (range === CURRENT_MONTH) getNextMonthOverviewData(latestUpcomingSpending);
      },
      () => {
        finishLoading();
        setActualSpendingData({...actualSpendingData, loaded: true});
      }
    ));
  }

  const getPreviousOverviewData = ({
    range = CURRENT_MONTH,
    query = undefined,
    variant = 'monthly'
  } = {}) => {
    const baseQuery = query || actualSpendingData.query;
  const endpointFunc = variant === 'monthly' ? overviewActions.getOverview : overviewActions.getYearlyOverview;

  const adjustedQuery = range === YEARLY
    ? overviewPageUtils.adjustPreviousQueryForYearly(baseQuery)
    : overviewPageUtils.adjustPreviousQueryForMonthly(baseQuery);

  dispatch(endpointFunc(
    adjustedQuery,
      (data) => {
        setExpectedSpendingData({
          ...expectedSpendingData,
          ...overviewPageUtils.getSpendingData(data),
          loaded: true,
        });
        finishLoading();
      },
      () => {
        finishLoading();
        setActualSpendingData({...actualSpendingData, loaded: true});
      }
    ));
  }

  const getOverviewTotals = (range = CURRENT_MONTH) => {
    if (range === YEARLY) return;

    let subtractMonthsCount = {
      [CURRENT_MONTH]: 0,
      [PREVIOUS_MONTH]: 1
    }[range] || 0;
    let totalValueKey = 'total_out';
    const getValue = (data) => data?.[totalValueKey] || null;

    startLoadingTotals();

    dispatch(transactionActions.getTransactionsTotals({
      query: subscriptionsHelpers.getYearMonthQueryParams(subtractMonthsCount),
      successCallback: (actualResponse) => {
        dispatch(transactionActions.getTransactionsTotals({
          query: subscriptionsHelpers.getYearMonthQueryParams(subtractMonthsCount + 1),
          successCallback: (expectedResponse) => {
            setTotals({
              actual: {value: getValue(actualResponse), isLoaded: true},
              expected: {value: getValue(expectedResponse), isLoaded: true}
            });
          },
          errorCallback: clearTotals
        }))
      },
      errorCallback: clearTotals
    }));
  }

  // const getNextMonthOverviewData = (latestUpcomingSpending) => {
  //   const nextDayRange = 10;
  //   const startDate = dayjs();
  //   const endDate = dayjs().add(nextDayRange, 'day');
  //   // load data for next month if end date is next month
  //   if (endDate.month() !== startDate.month()) {
  //     getOverview(
  //       {month: endDate.month() + 1, year: endDate.year()},
  //       (resp) => {
  //         const endDateDayNumber = endDate.date();
  //         const upcoming = (resp?.upcoming || []).filter(i => i.day <= endDateDayNumber);
  //         setUpcomingSpending([
  //           ...latestUpcomingSpending,
  //           ...overviewPageUtils.formatUpcoming(upcoming, endDate),
  //         ]);
  //       }
  //     )
  //   }
  // }

  const loadOverviewInformation = () => {
    getOverviewData();
    if (isTreezorEnabled) {
      getOverviewTotals();
    } else {
      setTotals({
        actual: {value: null, isLoaded: true},
        expected: {value: null, isLoaded: true}
      });
    }
  }

  useEffect(() => {
    if (isKnownEmployee && isFeaturesLoaded) {
      loadOverviewInformation();
    }
  }, [isKnownEmployee, isFeaturesLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isEmployeeChanged) {
      setShowLoader(true);
      setFilterProps({...filterProps, range: CURRENT_MONTH});
      loadOverviewInformation();
    }
  }, [isEmployeeChanged]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let isRequired = false;
    const isSupportedKycCountry = kycHelpers.isSupportedCountry(companyCountry);
    if (bankingCompanyState && isSupportedKycCountry) {
      isRequired = bankingCompanyState.stage !== 'card_stage';
      if (isRequired !== requiredCompleteKyc) setRequiredCompleteKyc(isRequired);
    }
    if (isRequired !== requiredCompleteKyc) setRequiredCompleteKyc(isRequired);
  }, [bankingCompanyState, companyCountry]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterSpending = (params) => {
    let variant = 'monthly';
    const range = params?.range;
    const isDefaultRequestType = checkIsDefaultRequestType(params);
    let query = {
      cost_centers: params?.tags || [],
      type: params?.type
    }
    if (params.range === YEARLY) {
      variant = 'yearly';
      const {date: todayDate} = getCurrentDate();
      const endDate = todayDate.format(dateFormat);
      const startDate = todayDate.subtract(11, 'month').format(dateFormat);
      query = {
        ...query,
        start_date: startDate,
        end_date: endDate
      };
    } else {
      const dateParams = getRangeDate(range);
      if (dateParams) query = { ...query, ...dateParams };
    }
    getOverviewData({range, query, variant});
    if (isDefaultRequestType && isTreezorEnabled) getOverviewTotals(range);
  }

  const expendituresItems = useMemo(() => {
    const getLabel = (key) => t(`subscriptions:overview.expendituresOptions.${key}`);
    return [
      {
        label: getLabel('all'),
        value: expendituresTypes.ALL,
      },
      {
        label: getLabel('subscriptions'),
        value: expendituresTypes.SUBSCRIPTIONS,
      },
      {
        label: getLabel('expenses'),
        value: expendituresTypes.EXPENSES,
      },
    ]
  }, [t]);

  const datePickerItems = useMemo(() => {
    const getDatePickerLabel = (key) => t(`subscriptions:overview.datePickerOptions.${key}`);
    return [
      {
        label: getDatePickerLabel('thisMonth'),
        key: CURRENT_MONTH,
      },
      {
        label: getDatePickerLabel('pastMonth'),
        key: PREVIOUS_MONTH,
      },
      {
        label: getDatePickerLabel('past12Months'),
        key: YEARLY,
      },
    ]
  }, [t]);

  const handleFilter = (key, value) => {
    let props = {...filterProps, [key]: value};
    setFilterProps(props);
    handleFilterSpending(props);
    systemHelpers.logEvent(firebaseEvents.OVERVIEW_USE_FILTER, {name: key});
  }

  const tagOptions = useMemo(() => formHelpers.getTagOptions(costCenterTags), [costCenterTags]);

  const spendingChartVariant = useMemo(() => {
    return chartView !== 'all' || filterProps.range === YEARLY ? 'barchart' : 'linechart';
  }, [chartView, filterProps]);

  const spendingProps = {
    enableTransaction: true,
    placeholder: false,
  }

  const spendingExtra = spendingProps.enableTransaction && (
    <Space size='small' align='center' className='spending-selectors'>
      <Select
        onChange={(value => handleFilter('type', value))}
        options={expendituresItems}
        value={filterProps.type}
      />
      <Select
        maxTagCount={1}
        mode='multiple'
        onChange={(value) => handleFilter('tags', value)}
        options={tagOptions}
        placeholder={t('costCenter')}
        size='middle'
      />
      <OverviewDropdown
        defaultSelected={filterProps.range}
        menuItems={datePickerItems}
        onSelect={(value) => handleFilter('range', value)}
        trigger={['click']}
      />
      <AddExpenseButton>+ {t('expense')}</AddExpenseButton>
    </Space>
  );

  const tabItems = [
    {
      key: 'expenses',
      label: t('expenses'),
      children: <Expenses handleOpenDetails={setIsOpenDetails} />
    },
    {
      key: 'budgets',
      label: t('budgets'),
      children: <Budgets />
    },
    {
      key: 'invoices',
      label: t('invoices'),
      children: <Invoices />
    }
  ];

  return (
    <StyledOverviewPageContainer>
      <PageDocumentDetails title={t('pageTitles.overview')} />
      <BalancePageHeader breadcrumbs={[{title: t('overview')}]} />
      <DashboardPageTransition>
        <StyledOverviewPageContent
          align='start'
          className={isOpenDetails ? 'full-details-open' : 'full-details-closed pr-0'}
          {...defaultSpaceProps}
        >
          <StyledOverviewPageSpin
            wrapperClassName='overview-main-spin'
            size='small'
            spinning={showLoader}
          >
            <Space
              align='start'
              direction='vertical'
              {...defaultSpaceProps}
            >
              <Space
                direction='vertical'
                {...defaultSpaceProps}
              >
                <UnmountClosed isOpened={requiredCompleteKyc}>
                  <CompleteKycCard variant='kyc' />
                </UnmountClosed>
                <UnmountClosed isOpened={isRequiredCardVerification}>
                  <CompleteKycCard variant='banking' />
                </UnmountClosed>
                <StyledOverviewPageCard
                  variant='outlined'
                  extra={spendingExtra}
                  title={isEmptySpending ? undefined : t('spending')}
                >
                  <Spending
                    barChartData={chartView === 'all' ? mainChartData.yearly : mainChartData.yearlyByServices}
                    chartVariant={spendingChartVariant}
                    lineChartData={chartData}
                    lineChartDataServices={mainChartData.chartDataServices}
                    counterProps={{
                      actualTimeRange,
                      data: counterData,
                      enableBudgetCounter: filterProps.range !== YEARLY
                    }}
                  />
                </StyledOverviewPageCard>
              </Space>

              {isFeaturesLoaded && (
                isTabsAvailable
                  ? <Tabs className='w-100' defaultActiveKey='expenses' items={tabItems} />
                  : <Expenses handleOpenDetails={setIsOpenDetails} />
              )}

            </Space>
          </StyledOverviewPageSpin>
          <AnimatePresence>
            <RightSidebar className='w-100' animate={isOpenDetails ? 'hidden' : 'visible'}>
              {/*<OverviewRightPanel*/}
              {/*  spendingDetailProps={{*/}
              {/*    ...otherSpendingData,*/}
              {/*    loading: showLoader,*/}
              {/*    upcoming: upcomingSpending,*/}
              {/*  }}*/}
              {/*/>*/}
            </RightSidebar>
          </AnimatePresence>
        </StyledOverviewPageContent>
      </DashboardPageTransition>
    </StyledOverviewPageContainer>
  );
}

export default OverviewPage;
